/**
 * Cart Drawer Component
 * Handles cart drawer open/close, rendering, and accessibility.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Sets up accessibility attributes and event listeners for cart icon.
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    const cartBar = document.querySelector('[cart__bar]');
    if (cartBar) {
      cartBar.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
    }

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Opens the cart drawer.
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  /**
   * Closes the cart drawer.
   */
  close() {
    this.classList.remove('active');
    
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    
    document.body.classList.remove('overflow-hidden');
  }

  /**
   * Sets accessibility attributes for summary elements.
   */
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  /**
   * Renders cart contents after product is added.
   */
  renderContents(parsedState) {
  const drawerInner = this.querySelector('.drawer__inner');
  if (drawerInner?.classList.contains('is-empty')) {
    drawerInner.classList.remove('is-empty');
  }

  this.productId = parsedState.id;

  // Update cart drawer
  const cartDrawer = document.getElementById('CartDrawer');
  if (cartDrawer && parsedState.sections?.['cart-drawer']) {
    const newContent = this.getSectionInnerHTML(parsedState.sections['cart-drawer'], '#CartDrawer');
    if (newContent) cartDrawer.innerHTML = newContent;
  }

  // Update cart icon bubble
  const cartBubbleSection = document.getElementById('shopify-section-cart-icon-bubble');
  if (cartBubbleSection && parsedState.sections?.['cart-icon-bubble']) {
    cartBubbleSection.innerHTML = parsedState.sections['cart-icon-bubble'];
  }

  setTimeout(() => {
    // this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this)); // Commented to prevent drawer opening on add
    // this.open(); // Commented to prevent drawer opening on add
    document.dispatchEvent(new CustomEvent('cart:updated'));
  });
}

  /**
   * Extracts inner HTML from a section response.
   * Returns null if selector not found instead of throwing.
   */
  getSectionInnerHTML(html, selector = '.js-contents') {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const element = doc.querySelector(selector);

  if (!element) {
    const shopifySection = doc.querySelector('.shopify-section');
    if (shopifySection) {
      return shopifySection.innerHTML;
    }
    return doc.body.innerHTML;
  }

  return element.innerHTML;
}

  /**
   * Returns sections that need to be re-rendered on cart update.
   */
  getSectionsToRender() {
  return [
    {
      id: 'cart-drawer',
      selector: '#CartDrawer'
    },
    {
      id: 'cart-icon-bubble',
      selector: '#shopify-section-cart-icon-bubble'
    }
  ];
}

  /**
   * Parses section HTML and returns DOM element.
   */
  getSectionDOM(html, selector = '.js-contents') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /**
   * Stores the element that triggered the drawer opening.
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


/**
 * Cart Drawer Items Component
 * Extends CartItems for drawer-specific section rendering.
 */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);